import { createContext, use, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Flex,
  Icon,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  type IconType,
} from '@kognitos/lattice';

/** One configuration area on the rail, e.g. Triggers or Integrations. */
export interface IAssetConfigRailItem {
  /**
   * Stable, untranslated id. A section that calls `useAssetConfigRailSection`
   * with the same id opens when this item's icon is clicked.
   */
  id: string;
  icon: IconType;
  /** The icon's tooltip and accessible name. */
  label: string;
  /** A badge on the icon, e.g. how many triggers are set up. Hidden at 0. */
  count?: number;
  /**
   * Work left in the section, e.g. automations still to build. Badged with a
   * dashed outline instead of `count`, and `label` becomes the tooltip.
   */
  todo?: { count: number; label: string };
}

/**
 * Props for {@link AssetConfigRail}.
 */
export interface IAssetConfigRailProps {
  /** The collapsed strip's icons, one per section. */
  items: IAssetConfigRailItem[];
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** The expanded rail: the sections, stacked, or a detail such as a selected step. */
  children: ReactNode;
  /**
   * The asset's primary actions, e.g. Run and Publish, at the top beside the
   * toggle: in a row left of it while expanded, stacked under it while
   * collapsed. Render them labelled or icon-only to match `isExpanded`.
   */
  actions?: ReactNode;
  /** Extra controls at the bottom of the collapsed strip, e.g. the selected step. */
  collapsedExtra?: ReactNode;
  /** Accessible name of the rail, e.g. "Configuration". */
  label: string;
  /** Mounted whether or not the rail is expanded, e.g. a file preview. */
  overlays?: ReactNode;
  'data-testid'?: string;
}

interface IRailOpenRequest {
  id: string | null;
  count: number;
}

// eslint-disable-next-line no-restricted-syntax -- reason: carries icon-click open requests to the sections in this rail's subtree; not app state
const OpenRequestContext = createContext<IRailOpenRequest | null>(null);

/**
 * How many times this section has been asked to open from the collapsed
 * strip. A section opens (and scrolls into view) whenever the number changes.
 *
 * @param id - The section's id, matching its rail item's `id`
 * @returns The request count, or 0 outside a rail
 */
export function useAssetConfigRailSection(id: string): number {
  const request = use(OpenRequestContext);
  return request?.id === id ? request.count : 0;
}

/**
 * The configuration rail on the right of the Definition view. Collapsed, it is
 * a strip of icons with counts; expanded, it shows every section stacked.
 * Clicking an icon expands the rail and opens that section. The asset's
 * primary actions sit at the top, beside the toggle.
 */
export function AssetConfigRail({
  items,
  isExpanded,
  onExpandedChange,
  children,
  actions,
  collapsedExtra,
  label,
  overlays,
  'data-testid': dataTestId = 'asset-config-rail',
}: IAssetConfigRailProps) {
  const { t } = useTranslation('common');
  const [openRequest, setOpenRequest] = useState<IRailOpenRequest>({
    id: null,
    count: 0,
  });
  const openRequestValue = useMemo(() => openRequest, [openRequest]);
  const toggleLabel = isExpanded
    ? t('common.assetWorkspace.collapseRail')
    : t('common.assetWorkspace.expandRail');

  const openItem = (id: string) => {
    setOpenRequest(previous => ({ id, count: previous.count + 1 }));
    onExpandedChange(true);
  };

  // Sections mount on expand and act on a pending request, so a plain
  // expand must not replay the last icon click.
  const toggleExpanded = () => {
    if (!isExpanded) {
      setOpenRequest(previous => ({ id: null, count: previous.count }));
    }
    onExpandedChange(!isExpanded);
  };

  return (
    <Flex
      direction="column"
      align={isExpanded ? 'stretch' : 'center'}
      gap={4}
      role="complementary"
      aria-label={label}
      className={cn(
        'h-full min-h-0 shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
        isExpanded ? 'w-[300px]' : 'w-11',
      )}
      data-state={isExpanded ? 'expanded' : 'collapsed'}
      data-testid={dataTestId}
    >
      {/* One container in both states, so the actions keep their mounts. */}
      <Flex
        direction={isExpanded ? 'row-reverse' : 'column'}
        align="center"
        gap={2}
        className={cn('shrink-0', isExpanded ? 'h-14' : 'py-3')}
        data-testid="asset-config-rail-header"
      >
        <Tooltip delayDuration={200} variant="primary">
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              onClick={toggleExpanded}
              aria-label={toggleLabel}
              aria-expanded={isExpanded}
              data-testid="asset-config-rail-toggle"
            >
              <Icon
                type={isExpanded ? 'ChevronRight' : 'ChevronLeft'}
                size="xs"
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{toggleLabel}</TooltipContent>
        </Tooltip>
        {actions && (
          <Flex
            direction={isExpanded ? 'row' : 'column'}
            align="center"
            gap={2}
            className="shrink-0 animate-in duration-200 fade-in zoom-in-95 motion-reduce:animate-none"
            data-testid="asset-config-rail-actions"
          >
            {actions}
          </Flex>
        )}
      </Flex>

      {isExpanded ? (
        <OpenRequestContext value={openRequestValue}>
          <Flex
            direction="column"
            align="stretch"
            gap={5}
            className="no-scrollbar min-h-0 flex-1 overflow-y-auto"
            data-testid="asset-config-rail-sections"
          >
            {children}
          </Flex>
        </OpenRequestContext>
      ) : (
        <Flex
          direction="column"
          align="center"
          gap={2}
          className="min-h-0 flex-1"
          data-testid="asset-config-rail-strip"
        >
          <Separator className="w-6" />
          {items.map(item => {
            const todoCount = item.todo?.count ?? 0;
            const name = todoCount && item.todo ? item.todo.label : item.label;
            return (
              <Tooltip key={item.id} delayDuration={200} variant="primary">
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={name}
                    onClick={() => openItem(item.id)}
                    className="relative shrink-0"
                    data-testid={`asset-config-rail-item-${item.id}`}
                  >
                    <Icon type={item.icon} size="sm" />
                    {todoCount ? (
                      <Badge
                        variant="outline"
                        className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full border-dashed border-foreground/50 bg-card px-1 text-xs tabular-nums"
                        data-testid={`asset-config-rail-todo-${item.id}`}
                      >
                        {todoCount}
                      </Badge>
                    ) : item.count ? (
                      <Badge
                        variant="secondary"
                        className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full px-1 text-xs tabular-nums"
                        data-testid={`asset-config-rail-count-${item.id}`}
                      >
                        {item.count}
                      </Badge>
                    ) : null}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">{name}</TooltipContent>
              </Tooltip>
            );
          })}
          <Flex flex="1" aria-hidden />
          {collapsedExtra}
        </Flex>
      )}
      {overlays}
    </Flex>
  );
}
